import * as React from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { AppUser } from '../../../../views/App/App';
import { Loader } from '../../../../components/Loader/Loader';
import { UnsavedChangesModal } from '../../../../components/UnsavedChangesModal/UnsavedChangesModal';
import { getClubProgramme } from '../../../../helpers/service/admin/clubProgrammes';
import { AfterSchoolClubProgramme } from '../../../../models/clubProgramme';
import { ClubProgrammeFieldsEditor } from './ClubProgrammeFieldsEditor';
import './ClubProgrammes.css';

type Props = {
  user: AppUser;
};

const getStatusLabel = (status: string | undefined): string => {
  const value = String(status || 'DRAFT').toLowerCase();
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

export const ClubProgrammeFieldsPage: React.FC<Props> = ({ user }) => {
  const { programmeId } = useParams<{ programmeId: string }>();
  const history = useHistory();
  const location = useLocation<any>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [programme, setProgramme] = React.useState<AfterSchoolClubProgramme | null>(null);
  const [error, setError] = React.useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [isLeaveWarningOpen, setIsLeaveWarningOpen] = React.useState(false);

  const loadProgramme = React.useCallback(() => {
    if (!programmeId) return;

    setIsLoading(true);
    setError('');

    getClubProgramme(user, String(programmeId))
      .then((p: AfterSchoolClubProgramme) => {
        setProgramme(p);
        setIsLoading(false);
      })
      .catch(e => {
        console.error('ClubProgrammeFieldsPage load error', e);
        setError('Failed to load programme.');
        setProgramme(null);
        setIsLoading(false);
      });
  }, [user, programmeId]);

  React.useEffect(() => {
    loadProgramme();
  }, [loadProgramme]);

  const goBack = () => {
    const backTo = location?.state?.from || {
      pathname: '/clubProgrammes',
      search: ''
    };

    history.push(backTo);
  };

  const requestBack = () => {
    if (hasUnsavedChanges) {
      setIsLeaveWarningOpen(true);
      return;
    }
    goBack();
  };

  if (isLoading) return <Loader />;

  const status = String(programme?.status || 'DRAFT').toUpperCase();

  return (
    <div className="cpProgrammeFieldsPage">
      <header className="cpProgrammeFieldsPageHeader">
        <button type="button" className="cpProgrammeFieldsBack" onClick={requestBack}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m14.5 5-7 7 7 7" />
          </svg>
          <span>Back to activity programmes</span>
        </button>

        <div className="cpProgrammeFieldsHeading">
          <h1>{String(programme?.name || 'Programme')}</h1>
          {programme ? (
            <span className={`cpProgrammeFieldsStatus ${status === 'DRAFT' ? 'isDraft' : 'isActive'}`}>
              {getStatusLabel(programme.status)}
            </span>
          ) : null}
          <span className="cpProgrammeFieldsContext">Booking form fields</span>
        </div>
      </header>

      <main className="cpProgrammeFieldsPageBody">
        {error || !programme ? (
          <div className="cpProgrammeFieldsLoadError" role="alert">
            {error || 'Programme was not found.'}
          </div>
        ) : (
          <ClubProgrammeFieldsEditor
            user={user}
            programme={programme}
            onProgrammeUpdated={setProgramme}
            onDirtyChange={setHasUnsavedChanges}
            mode="FULL"
          />
        )}
      </main>

      <UnsavedChangesModal
        isOpen={isLeaveWarningOpen}
        message="The changes to this booking form have not been saved. If you leave now, they will be lost."
        onKeepEditing={() => setIsLeaveWarningOpen(false)}
        onDiscard={() => {
          setIsLeaveWarningOpen(false);
          goBack();
        }}
      />
    </div>
  );
};
